/**
 * What may be said, and by whom.
 *
 * Shared because the admin console offers the choices and the worker has to reject anything
 * else: a speaker the model does not know is an error after the money is spent, and a
 * validation failure before it.
 */

/** Long enough for any reminder, short enough that a slip cannot spend the daily allowance. */
export const SPEECH_MAX_CHARACTERS = 300;

/** Aura 2's speakers, as published for the model. */
export const SPEECH_SPEAKERS = [
	"amalthea", "andromeda", "apollo", "arcas", "aries", "asteria", "athena", "atlas", "aurora",
	"callista", "cora", "cordelia", "delia", "draco", "electra", "harmonia", "helena", "hera",
	"hermes", "hyperion", "iris", "janus", "juno", "jupiter", "luna", "mars", "minerva", "neptune",
	"odysseus", "ophelia", "orion", "orpheus", "pandora", "phoebe", "pluto", "saturn", "thalia",
	"theia", "vesta", "zeus",
] as const;

export type SpeechSpeaker = (typeof SPEECH_SPEAKERS)[number];

export type SpeechAccent = "British" | "Australian" | "Filipino" | "American";

export interface SpeechVoice {
	gender: "feminine" | "masculine";
	accent: SpeechAccent;
	notes: string;
}

/**
 * Who each speaker sounds like, from Deepgram's published voice table.
 *
 * Worth having in front of you: only two of the forty are British and two Australian, so the
 * choice is really "which American" unless you go looking.
 */
export const SPEECH_VOICES: Record<SpeechSpeaker, SpeechVoice> = {
	amalthea: { gender: "feminine", accent: "Filipino", notes: "Engaging, natural, cheerful" },
	andromeda: { gender: "feminine", accent: "American", notes: "Casual, expressive, comfortable" },
	apollo: { gender: "masculine", accent: "American", notes: "Confident, comfortable, casual" },
	arcas: { gender: "masculine", accent: "American", notes: "Natural, smooth, clear" },
	aries: { gender: "masculine", accent: "American", notes: "Warm, energetic, caring" },
	asteria: { gender: "feminine", accent: "American", notes: "Clear, confident, energetic" },
	athena: { gender: "feminine", accent: "American", notes: "Calm, smooth, professional, mature" },
	atlas: { gender: "masculine", accent: "American", notes: "Enthusiastic, approachable, mature" },
	aurora: { gender: "feminine", accent: "American", notes: "Cheerful, expressive, energetic" },
	callista: { gender: "feminine", accent: "American", notes: "Clear, energetic, professional" },
	cora: { gender: "feminine", accent: "American", notes: "Smooth, melodic, caring" },
	cordelia: { gender: "feminine", accent: "American", notes: "Approachable, warm, polite" },
	delia: { gender: "feminine", accent: "American", notes: "Casual, friendly, cheerful, breathy" },
	draco: { gender: "masculine", accent: "British", notes: "Warm, approachable, trustworthy, baritone" },
	electra: { gender: "feminine", accent: "American", notes: "Professional, engaging, knowledgeable" },
	harmonia: { gender: "feminine", accent: "American", notes: "Empathetic, clear, calm" },
	helena: { gender: "feminine", accent: "American", notes: "Caring, positive, friendly, raspy" },
	hera: { gender: "feminine", accent: "American", notes: "Smooth, warm, professional" },
	hermes: { gender: "masculine", accent: "American", notes: "Expressive, engaging, professional" },
	hyperion: { gender: "masculine", accent: "Australian", notes: "Caring, warm, empathetic" },
	iris: { gender: "feminine", accent: "American", notes: "Cheerful, positive, approachable" },
	janus: { gender: "feminine", accent: "American", notes: "Southern, smooth, trustworthy" },
	juno: { gender: "feminine", accent: "American", notes: "Natural, engaging, melodic, breathy" },
	jupiter: { gender: "masculine", accent: "American", notes: "Expressive, knowledgeable, baritone" },
	luna: { gender: "feminine", accent: "American", notes: "Friendly, natural, engaging" },
	mars: { gender: "masculine", accent: "American", notes: "Smooth, patient, trustworthy, baritone" },
	minerva: { gender: "feminine", accent: "American", notes: "Positive, friendly, natural" },
	neptune: { gender: "masculine", accent: "American", notes: "Professional, patient, polite" },
	odysseus: { gender: "masculine", accent: "American", notes: "Calm, smooth, professional" },
	ophelia: { gender: "feminine", accent: "American", notes: "Expressive, enthusiastic, cheerful" },
	orion: { gender: "masculine", accent: "American", notes: "Approachable, calm, polite" },
	orpheus: { gender: "masculine", accent: "American", notes: "Professional, clear, confident" },
	pandora: { gender: "feminine", accent: "British", notes: "Smooth, calm, melodic, breathy" },
	phoebe: { gender: "feminine", accent: "American", notes: "Energetic, warm, casual" },
	pluto: { gender: "masculine", accent: "American", notes: "Smooth, calm, empathetic, baritone" },
	saturn: { gender: "masculine", accent: "American", notes: "Knowledgeable, confident, baritone" },
	thalia: { gender: "feminine", accent: "American", notes: "Clear, confident, enthusiastic" },
	theia: { gender: "feminine", accent: "Australian", notes: "Expressive, polite, sincere" },
	vesta: { gender: "feminine", accent: "American", notes: "Natural, expressive, empathetic" },
	zeus: { gender: "masculine", accent: "American", notes: "Deep, trustworthy, smooth" },
};

/** Rarest first, so the handful that are not American are the ones you see. */
export const SPEECH_ACCENTS: SpeechAccent[] = ["British", "Australian", "Filipino", "American"];

export const DEFAULT_SPEAKER: SpeechSpeaker = "luna";

export function isSpeechSpeaker(value: unknown): value is SpeechSpeaker {
	return typeof value === "string" && (SPEECH_SPEAKERS as readonly string[]).includes(value);
}

export interface SpeechRequest {
	text: string;
	speaker: SpeechSpeaker;
}

/** Rejects before spending anything, so a bad request costs nothing. */
export function readSpeechRequest(body: unknown): SpeechRequest | { error: string } {
	const raw = body as { text?: unknown; speaker?: unknown } | null;
	const text = typeof raw?.text === "string" ? raw.text.trim() : "";
	if (text.length === 0) {
		return { error: "Enter something to say." };
	}
	if (text.length > SPEECH_MAX_CHARACTERS) {
		return { error: `Keep it under ${SPEECH_MAX_CHARACTERS} characters (that was ${text.length}).` };
	}
	const speaker = raw?.speaker === undefined ? DEFAULT_SPEAKER : raw.speaker;
	if (!isSpeechSpeaker(speaker)) {
		return { error: "Unknown speaker." };
	}
	return { text, speaker };
}
